import React, { useState, useEffect } from 'react';
import { Alert, Modal, Select, Button, Space, message, Table, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { UserService, User } from '../../services/api/userService';
import { UserSubscription } from '../../types/subscription';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { Option } = Select;
const { Text, Paragraph } = Typography;

interface AssignSubscriptionDialogProps {
  visible: boolean;
  /** Company subscription id (ownership row) for seat assign. */
  subscriptionId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Seat assignment dialog (layer C): users ↔ company subscription.
 * Company plan creation is Company Management → Subscriptions (POST /subscriptions/companies).
 */
export const AssignSubscriptionDialog: React.FC<AssignSubscriptionDialogProps> = ({
  visible,
  subscriptionId,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<UserSubscription[]>([]);

  useEffect(() => {
    if (visible) {
      void loadData();
    }
  }, [visible, subscriptionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersList, seats] = await Promise.all([
        UserService.getAll(),
        SubscriptionService.getCompanySubscriptionSeats(subscriptionId),
      ]);
      setUsers(usersList);
      setAssignedUsers(seats);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error(getApiErrorMessage(error, 'Failed to load assignment data'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUsers = async () => {
    if (selectedUserIds.length === 0) {
      message.warning('Please select at least one user');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        selectedUserIds.map((userId) =>
          SubscriptionService.assignUser(subscriptionId, { user_id: userId })
        )
      );
      message.success(`Assigned subscription to ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      await loadData();
      onSuccess();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Failed to assign users'));
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignUser = async (userId: string) => {
    try {
      await SubscriptionService.unassignUser(subscriptionId, userId);
      message.success('User unassigned successfully');
      await loadData();
      onSuccess();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Failed to unassign user'));
    }
  };

  const availableUsers = users.filter(
    (user) => !assignedUsers.some((au) => au.user_id === user.id)
  );

  const userColumns = [
    {
      title: 'Username',
      key: 'username',
      render: (record: UserSubscription) => record.user?.username || 'N/A',
    },
    {
      title: 'Email',
      key: 'email',
      render: (record: UserSubscription) => record.user?.email || 'N/A',
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: UserSubscription) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => void handleUnassignUser(record.user_id)}
        >
          Unassign
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Assign seats"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Seats on a company subscription"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            This assigns users to an existing <Text strong>company subscription</Text> id. Create
            company plans from Company Management → Subscriptions (
            <Text code>POST /subscriptions/companies</Text>). User-owned plans are created under
            User Management → Subscriptions.
          </Paragraph>
        }
      />
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text strong>Assign to Users</Text>
          <Select
            mode="multiple"
            placeholder="Select users to assign"
            style={{ width: '100%', marginTop: 8 }}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {availableUsers.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.username} ({user.email})
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => void handleAssignUsers()}
            style={{ marginTop: 8 }}
            loading={loading}
            disabled={selectedUserIds.length === 0}
          >
            Assign Selected Users
          </Button>
        </div>

        <div>
          <Text strong>Currently Assigned Users</Text>
          <Table
            columns={userColumns}
            dataSource={assignedUsers}
            rowKey="id"
            pagination={false}
            size="small"
            style={{ marginTop: 8 }}
          />
        </div>
      </Space>
    </Modal>
  );
};
